import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check, CheckCheck, Trash2, AlertTriangle, Info, BellOff, MessageCircleMore } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNotifications } from "@/hooks/useNotifications";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const typeIcons: Record<string, typeof Bell> = {
  deadline: AlertTriangle,
  assignment: Info,
  approval: Check,
  mention: MessageCircleMore,
  info: Bell,
};

export function NotificationBell() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [notificationPendingDelete, setNotificationPendingDelete] = useState<(typeof notifications)[0] | null>(null);
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  } = useNotifications();

  const handleClick = (notification: (typeof notifications)[0]) => {
    if (!notification.is_read) {
      markAsRead.mutate(notification.id);
    }
    if (notification.link) {
      setOpen(false);
      navigate(notification.link);
    }
  };

  const notificationContent = (
    <>
      <div className="flex items-center justify-between px-4 py-3 border-b border-border/50 bg-muted/20">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold">Notificações</span>
          {unreadCount > 0 && (
            <span className="px-1.5 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-bold">
              {unreadCount} novas
            </span>
          )}
        </div>
        {unreadCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs gap-1.5 text-muted-foreground hover:text-primary"
            onClick={() => markAllAsRead.mutate()}
          >
            <CheckCheck className="h-3 w-3" />
            Marcar lidas
          </Button>
        )}
      </div>

      <ScrollArea className={isMobile ? "h-[65vh]" : "max-h-[400px]"}>
        {notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-3">
              <BellOff className="h-6 w-6 text-muted-foreground/50" />
            </div>
            <p className="text-sm font-medium text-foreground">Tudo limpo por aqui!</p>
            <p className="text-xs text-muted-foreground mt-1 max-w-[200px]">
              Você leu todas as suas notificações. Aproveite o foco extra.
            </p>
          </div>
        ) : (
          <div className="py-1">
            {notifications.map((n) => {
              const Icon = typeIcons[n.type] || Bell;
              return (
                <div
                  key={n.id}
                  className={`group relative flex items-start gap-4 border-l-2 px-4 py-3 transition-colors ${
                    !n.is_read
                      ? "bg-primary/5 border-primary"
                      : "border-transparent hover:bg-muted/50"
                  }`}
                >
                  <button className="flex flex-1 items-start gap-4 text-left" onClick={() => handleClick(n)}>
                    <div className={`mt-0.5 p-2 rounded-full shrink-0 ${
                      n.type === "deadline" ? "bg-destructive/10 text-destructive" :
                      n.type === "assignment" ? "bg-info/10 text-info" :
                      n.type === "approval" ? "bg-success/10 text-success" :
                      n.type === "mention" ? "bg-primary/10 text-primary" :
                      "bg-muted text-muted-foreground"
                    }`}>
                      <Icon className="h-4 w-4" />
                    </div>

                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm leading-tight ${!n.is_read ? "font-semibold text-foreground" : "font-medium text-foreground/80"}`}>
                          {n.title}
                        </p>
                        <span className="text-[10px] text-muted-foreground shrink-0 whitespace-nowrap">
                          {formatDistanceToNow(new Date(n.created_at), {
                            addSuffix: false,
                            locale: ptBR,
                          })}
                        </span>
                      </div>

                      {n.message && (
                        <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">
                          {n.message}
                        </p>
                      )}
                    </div>
                  </button>

                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                    onClick={(e) => {
                      e.stopPropagation();
                      setNotificationPendingDelete(n);
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </ScrollArea>

      {notifications.length > 0 && (
        <div className="p-2 border-t border-border/50 bg-muted/10">
          <Button variant="ghost" className="w-full h-10 text-sm text-muted-foreground hover:text-foreground">
            Ver histórico completo
          </Button>
        </div>
      )}
    </>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <Button variant="ghost" size="icon" className="relative rounded-full hover:bg-muted/50 transition-colors">
            <Bell className="h-5 w-5 text-muted-foreground" />
            {unreadCount > 0 && (
              <span className="absolute top-1.5 right-1.5 h-2.5 w-2.5 bg-primary rounded-full ring-2 ring-background animate-pulse" />
            )}
          </Button>
        </SheetTrigger>
        <SheetContent side="bottom" className="h-[80vh] rounded-t-3xl p-0">
          <SheetHeader className="px-4 py-4 text-left border-b border-border/50">
            <SheetTitle>Notificações</SheetTitle>
          </SheetHeader>
          {notificationContent}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative rounded-full hover:bg-muted/50 transition-colors">
          <Bell className="h-5 w-5 text-muted-foreground" />
          {unreadCount > 0 && (
            <span className="absolute top-1.5 right-1.5 h-2.5 w-2.5 bg-primary rounded-full ring-2 ring-background animate-pulse" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 bg-popover z-50 p-0 shadow-xl border-border/50">
        {notificationContent}
      </DropdownMenuContent>

      <AlertDialog open={!!notificationPendingDelete} onOpenChange={() => setNotificationPendingDelete(null)}>
        <AlertDialogContent className="flex max-h-[90vh] flex-col overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir notificação?</AlertDialogTitle>
            <AlertDialogDescription>
              A notificação <strong>{notificationPendingDelete?.title}</strong> será removida do seu histórico.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (notificationPendingDelete) {
                  deleteNotification.mutate(notificationPendingDelete.id);
                }
                setNotificationPendingDelete(null);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir notificação
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DropdownMenu>
  );
}
